// ============================================================
// Stocora — ReceivePage (Pilot Task 6, #3, 2026-06-17)
// ============================================================
// Manual "mark as received" screen (Drew's Option A / manual-
// confirm). Reached from a sent order on ReorderPage's Sent tab
// via /receive/:orderId. Nav stays 5 tabs — this is a standalone
// screen, not a new tab (Drew's locked structure 2026-06-17).
//
// Flow:
//   1. Load the order + its lines (ordered vs already received).
//   2. Default every line to its remaining qty = "received in
//      full". The user can adjust any line down for a short
//      delivery. Short lines stay on the order (partial_received).
//   3. "Confirm what arrived" → receive_order RPC. The RPC clamps
//      each line to remaining, so this is idempotent.
//
// Any staff may receive (ratified UX 2026-05-28). The receiver
// lands on the inventory_events.actor_id audit trail, which is
// the manager-visible record (Tier B — no separate notification
// primitive in pilot).
// ============================================================

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useSession } from '@/lib/session';
import {
  loadReceivableOrder,
  receiveOrder,
  type ReceivableOrder,
  type ReceivableLine,
  type ReceiveResult,
} from '@/lib/receiving';

type ReceiveState =
  | { kind: 'loading' }
  | { kind: 'not_found' }
  | { kind: 'load_error'; message: string }
  | { kind: 'ready'; order: ReceivableOrder }
  | { kind: 'confirming'; order: ReceivableOrder }
  | { kind: 'confirm_error'; order: ReceivableOrder; message: string }
  | { kind: 'done'; order: ReceivableOrder; result: ReceiveResult };

export default function ReceivePage() {
  const params = useParams<{ orderId: string }>();
  const orderId = params.orderId ?? '';

  if (orderId === '') {
    return <Navigate to="/reorder" replace />;
  }
  return <Receive orderId={orderId} />;
}

function Receive({ orderId }: { orderId: string }) {
  const navigate = useNavigate();
  const { activeMembership, userId } = useSession();
  const clinicId = activeMembership.clinic_id;

  const [state, setState] = useState<ReceiveState>({ kind: 'loading' });
  // Per-line received quantity, keyed by order_item_id. Seeded to
  // each line's remaining qty ("received in full") on load.
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await loadReceivableOrder(clinicId, orderId);
      if (cancelled) return;
      if (res.kind === 'not_found') {
        setState({ kind: 'not_found' });
        return;
      }
      if (res.kind === 'error') {
        setState({ kind: 'load_error', message: res.message });
        return;
      }
      const seed: Record<string, number> = {};
      for (const line of res.order.lines) {
        seed[line.order_item_id] = line.qty_remaining;
      }
      setQuantities(seed);
      setState({ kind: 'ready', order: res.order });
    })();
    return () => {
      cancelled = true;
    };
  }, [clinicId, orderId]);

  const setLineQty = useCallback(
    (line: ReceivableLine, value: number) => {
      const clamped = Math.max(0, Math.min(value, line.qty_remaining));
      setQuantities((prev) => ({ ...prev, [line.order_item_id]: clamped }));
    },
    [],
  );

  const receiveAllInFull = useCallback((order: ReceivableOrder) => {
    const full: Record<string, number> = {};
    for (const line of order.lines) {
      full[line.order_item_id] = line.qty_remaining;
    }
    setQuantities(full);
  }, []);

  const handleConfirm = useCallback(
    async (order: ReceivableOrder) => {
      const lines = order.lines
        .map((l) => ({
          order_item_id: l.order_item_id,
          qty: quantities[l.order_item_id] ?? 0,
        }))
        .filter((l) => l.qty > 0);

      if (lines.length === 0) {
        setState({
          kind: 'confirm_error',
          order,
          message: 'Nothing to receive — set a quantity for at least one item.',
        });
        return;
      }

      setState({ kind: 'confirming', order });
      const outcome = await receiveOrder(clinicId, orderId, lines, userId);
      if (outcome.kind === 'error') {
        setState({ kind: 'confirm_error', order, message: outcome.message });
        return;
      }
      setState({ kind: 'done', order, result: outcome.result });
    },
    [clinicId, orderId, quantities, userId],
  );

  // ---- Render ----------------------------------------------------

  if (state.kind === 'loading') {
    return (
      <Shell>
        <div className="flex justify-center py-12">
          <span className="text-[12px] text-slate-500">Loading order…</span>
        </div>
      </Shell>
    );
  }

  if (state.kind === 'not_found') {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 py-10">
          <p className="text-[13px] text-slate-600 text-center">
            This order couldn’t be found.
          </p>
          <button
            onClick={() => navigate('/reorder')}
            className="h-11 px-5 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 transition text-sm font-medium text-slate-700"
          >
            Back to orders
          </button>
        </div>
      </Shell>
    );
  }

  if (state.kind === 'load_error') {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 py-10">
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-[12px] text-red-800 leading-relaxed text-center">
            Couldn’t load this order. {state.message}
          </div>
          <button
            onClick={() => navigate('/reorder')}
            className="h-11 px-5 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 transition text-sm font-medium text-slate-700"
          >
            Back to orders
          </button>
        </div>
      </Shell>
    );
  }

  if (state.kind === 'done') {
    const { result, order } = state;
    const fullyReceived = result.order_status === 'received';
    return (
      <Shell>
        <div className="flex flex-col items-center gap-2 py-8">
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center ${
              fullyReceived ? 'bg-emerald-50' : 'bg-amber-50'
            }`}
          >
            <span className="text-2xl">{fullyReceived ? '✓' : '◐'}</span>
          </div>
          <h2 className="text-base font-semibold text-slate-900 mt-1">
            {fullyReceived ? 'Order received in full' : 'Partial delivery recorded'}
          </h2>
          <p className="text-[12px] text-slate-600 text-center leading-relaxed px-2">
            {result.units_credited} unit{result.units_credited === 1 ? '' : 's'} added to
            inventory across {result.lines_credited} item
            {result.lines_credited === 1 ? '' : 's'} for {order.vendor_name}.
            {!fullyReceived && ' Short items stay on the order until the rest arrives.'}
          </p>
          <button
            onClick={() => navigate('/reorder')}
            className="mt-4 h-11 px-5 rounded-xl bg-[#2A7F8E] hover:bg-[#246e7b] transition text-sm font-medium text-white"
          >
            Done
          </button>
        </div>
      </Shell>
    );
  }

  // ready | confirming | confirm_error all render the line editor.
  const order = state.order;
  const busy = state.kind === 'confirming';

  return (
    <Shell vendorName={order.vendor_name} onBack={() => navigate('/reorder')}>
      {state.kind === 'confirm_error' && (
        <div className="mb-3 bg-red-50 border border-red-200 rounded-xl p-3 text-[12px] text-red-800 leading-relaxed">
          {state.message}
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <span className="text-[13px] font-semibold text-slate-900">What arrived?</span>
        <button
          onClick={() => receiveAllInFull(order)}
          disabled={busy}
          className="text-[12px] font-medium text-[#2A7F8E] hover:underline disabled:opacity-50"
        >
          Received in full
        </button>
      </div>

      <div className="space-y-2">
        {order.lines.map((line) => {
          const value = quantities[line.order_item_id] ?? 0;
          const alreadyDone = line.qty_remaining === 0;
          return (
            <div
              key={line.order_item_id}
              className="bg-white rounded-2xl border border-slate-200 p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-semibold text-slate-900 leading-tight">
                    {line.product_name}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {alreadyDone
                      ? `All ${line.qty_ordered} ${line.uom} received`
                      : `${line.qty_received} of ${line.qty_ordered} ${line.uom} received so far`}
                  </p>
                </div>
                {!alreadyDone && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      aria-label="Decrease"
                      onClick={() => setLineQty(line, value - 1)}
                      disabled={busy || value <= 0}
                      className="w-8 h-8 rounded-lg border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={value}
                      min={0}
                      max={line.qty_remaining}
                      disabled={busy}
                      onChange={(e) =>
                        setLineQty(line, parseInt(e.target.value || '0', 10))
                      }
                      className="w-12 h-8 text-center text-[14px] font-semibold text-slate-900 border border-slate-200 rounded-lg"
                    />
                    <button
                      aria-label="Increase"
                      onClick={() => setLineQty(line, value + 1)}
                      disabled={busy || value >= line.qty_remaining}
                      className="w-8 h-8 rounded-lg border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50"
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={() => handleConfirm(order)}
        disabled={busy}
        className="mt-4 w-full h-12 rounded-xl bg-[#2A7F8E] hover:bg-[#246e7b] disabled:opacity-60 transition text-sm font-semibold text-white"
      >
        {busy ? 'Confirming…' : 'Confirm what arrived'}
      </button>
    </Shell>
  );
}

// ---- Shell: header + back, matches app chrome -------------------

function Shell({
  children,
  vendorName,
  onBack,
}: {
  children: React.ReactNode;
  vendorName?: string;
  onBack?: () => void;
}) {
  return (
    <div className="p-4 pb-8 max-w-md mx-auto">
      {onBack && (
        <button
          onClick={onBack}
          className="text-[13px] font-medium text-[#2A7F8E] mb-2"
        >
          ‹ Orders
        </button>
      )}
      {vendorName && (
        <h1 className="text-lg font-bold text-slate-900 mb-1">
          Receive — {vendorName}
        </h1>
      )}
      {children}
    </div>
  );
}
